import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import ConfirmDialog from '../ui/ConfirmDialog';
import { AppIconButton, SecondaryButton } from '../ui';

export interface CategoryManagerItem {
  id: number | string;
  name: string;
  group?: string;
  isSystem?: boolean;
}

export interface CategoryManagerDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Display order of group headers */
  groups: string[];
  items: CategoryManagerItem[];
  /** Create new item under a specific group (or no group for vendor-custom). */
  onCreate: (name: string, group?: string) => Promise<void> | void;
  onRename: (id: CategoryManagerItem['id'], name: string) => Promise<void> | void;
  onDelete: (id: CategoryManagerItem['id']) => Promise<void> | void;
}

/**
 * Tree-style drawer that lets a vendor add / rename / delete categories
 * grouped by section header. System rows are read-only. Backend CRUD is
 * surfaced through the parent's create / rename / delete callbacks.
 */
const CategoryManagerDrawer: React.FC<CategoryManagerDrawerProps> = ({
  open,
  onClose,
  title = 'Manage Categories',
  groups,
  items,
  onCreate,
  onRename,
  onDelete,
}) => {
  const { theme } = useTheme();

  const [editingId, setEditingId] = useState<CategoryManagerItem['id'] | null>(null);
  const [editingName, setEditingName] = useState('');
  const [addingGroup, setAddingGroup] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<CategoryManagerItem['id'] | null>(null);
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, CategoryManagerItem[]>();
    for (const g of groups) map.set(g, []);
    for (const item of items) {
      const g = item.group ?? '';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return Array.from(map.entries()).filter(([g]) => g !== '' || (map.get('') ?? []).length > 0);
  }, [groups, items]);

  const startEdit = (item: CategoryManagerItem) => {
    setEditingId(item.id);
    setEditingName(item.name);
  };

  const submitEdit = async () => {
    if (editingId === null || !editingName.trim()) return;
    setBusy(true);
    try {
      await onRename(editingId, editingName.trim());
      setEditingId(null);
      setEditingName('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Rename failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const startAdd = (group: string) => {
    setAddingGroup(group);
    setNewName('');
  };

  const submitAdd = async () => {
    if (addingGroup === null || !newName.trim()) return;
    setBusy(true);
    try {
      await onCreate(newName.trim(), addingGroup || undefined);
      setAddingGroup(null);
      setNewName('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (pendingDeleteId === null) return;
    setBusy(true);
    try {
      await onDelete(pendingDeleteId);
      setPendingDeleteId(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>{title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {grouped.map(([group, list]) => (
          <Box key={group || '__none__'} sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: theme.gray?.[500] || '#8A8A8A',
                mb: 0.75,
              }}
            >
              {group || 'Uncategorized'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {list.map((item) => {
                const isEditing = editingId === item.id;
                const readOnly = item.isSystem === true;
                if (isEditing) {
                  return (
                    <Stack
                      key={String(item.id)}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <TextField
                        autoFocus
                        size="small"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitEdit();
                          if (e.key === 'Escape') {
                            setEditingId(null);
                            setEditingName('');
                          }
                        }}
                        sx={{ flex: 1 }}
                      />
                      <AppIconButton
                        icon={<CheckIcon fontSize="small" />}
                        tooltip="Save"
                        variant="success"
                        size="small"
                        onClick={submitEdit}
                        disabled={busy}
                      />
                      <AppIconButton
                        icon={<CloseIcon fontSize="small" />}
                        tooltip="Cancel"
                        variant="ghost"
                        size="small"
                        onClick={() => {
                          setEditingId(null);
                          setEditingName('');
                        }}
                      />
                    </Stack>
                  );
                }
                return (
                  <Box
                    key={String(item.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      pl: 1,
                      borderRadius: 1,
                      '&:hover': { backgroundColor: `${theme.primary}05` },
                    }}
                  >
                    <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>{item.name}</Typography>
                    {readOnly ? (
                      <Chip
                        label="System"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          backgroundColor: theme.gray?.[200],
                          color: theme.gray?.[600],
                        }}
                      />
                    ) : (
                      <>
                        <Tooltip title="Rename">
                          <IconButton size="small" onClick={() => startEdit(item)}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setPendingDeleteId(item.id)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                );
              })}

              {addingGroup === group ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5 }}>
                  <TextField
                    autoFocus
                    size="small"
                    placeholder={`New ${group || 'category'}`}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitAdd();
                      if (e.key === 'Escape') {
                        setAddingGroup(null);
                        setNewName('');
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                  <AppIconButton
                    icon={<CheckIcon fontSize="small" />}
                    tooltip="Add"
                    variant="success"
                    size="small"
                    onClick={submitAdd}
                    disabled={busy || !newName.trim()}
                  />
                  <AppIconButton
                    icon={<CloseIcon fontSize="small" />}
                    tooltip="Cancel"
                    variant="ghost"
                    size="small"
                    onClick={() => {
                      setAddingGroup(null);
                      setNewName('');
                    }}
                  />
                </Stack>
              ) : (
                <SecondaryButton
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => startAdd(group)}
                  sx={{ alignSelf: 'flex-start', fontSize: '0.75rem' }}
                >
                  Add to {group || 'list'}
                </SecondaryButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Delete category?"
        message="Existing records using this category stay intact, but it will no longer appear in the picker."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={busy}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </Drawer>
  );
};

export default CategoryManagerDrawer;
